import React, { useState, useEffect, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { auth } from "./firebase-config";
import { getUserRoleAndStatus } from "./services/authService";

import Navbar from "./Components/Navbar";
import Footer from "./Components/Footer";
import Home from "./Pages/Home";

// Code splitting & lazy loading heavy route components for performance
const Services = lazy(() => import("./Pages/Services"));
const CourseDetails = lazy(() => import("./Pages/CourseDetails"));
const AdminLogin = lazy(() => import("./Pages/AdminLogin"));
const AdminDashboard = lazy(() => import("./Pages/AdminDashboard"));
const ContactUs = lazy(() => import("./Pages/ContactUs"));
const Gallery = lazy(() => import("./Pages/Gallery"));
const StudentLogin = lazy(() => import("./Pages/StudentLogin"));
const StudentDashboard = lazy(() => import("./Pages/StudentDashboard"));
const StudentAcademicHistory = lazy(() => import("./Pages/StudentAcademicHistory"));
const InstructorLogin = lazy(() => import("./Pages/InstructorLogin"));
const InstructorDashboard = lazy(() => import("./Pages/InstructorDashboard"));
const InstructorForcePasswordChange = lazy(() => import("./Pages/InstructorForcePasswordChange"));

// Fallback Spinner for Suspense
const PageLoader = () => (
  <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", minHeight: "50vh" }}>
    <div style={{ width: "36px", height: "36px", border: "3px solid #e2e8f0", borderTop: "3px solid #000000", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
    <p style={{ marginTop: "14px", fontSize: "14px", color: "#4a5568", fontWeight: 600 }}>Loading page...</p>
    <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
  </div>
);

/**
 * Generic Role-Based Protected Route Guard.
 * Shows loading spinner while evaluating Firebase Auth state and Firestore/Custom Claims roles.
 * Never renders children before role verification completes.
 */
function RoleProtectedRoute({ children, requiredRole, redirectPath }) {
  const [loading, setLoading] = useState(true);
  const [authorized, setAuthorized] = useState(false);

  useEffect(() => {
    let isMounted = true;
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        if (isMounted) {
          setAuthorized(false);
          setLoading(false);
        }
        return;
      }

      try {
        const { role, isDeactivated } = await getUserRoleAndStatus(user);
        if (isMounted) {
          if (role === requiredRole && !isDeactivated) {
            setAuthorized(true);
          } else {
            await signOut(auth);
            localStorage.clear();
            sessionStorage.clear();
            setAuthorized(false);
          }
          setLoading(false);
        }
      } catch (err) {
        if (isMounted) {
          await signOut(auth);
          localStorage.clear();
          sessionStorage.clear();
          setAuthorized(false);
          setLoading(false);
        }
      }
    });

    return () => {
      isMounted = false;
      unsubscribe();
    };
  }, [requiredRole]);

  if (loading) {
    return <PageLoader />;
  }

  if (!authorized) {
    return <Navigate to={redirectPath} replace />;
  }

  return children;
}

function AdminRoute({ children }) {
  return (
    <RoleProtectedRoute requiredRole="admin" redirectPath="/admin">
      {children}
    </RoleProtectedRoute>
  );
}

function InstructorRoute({ children }) {
  return (
    <RoleProtectedRoute requiredRole="instructor" redirectPath="/instructor/login">
      {children}
    </RoleProtectedRoute>
  );
}

function StudentRoute({ children }) {
  return (
    <RoleProtectedRoute requiredRole="student" redirectPath="/student/login">
      {children}
    </RoleProtectedRoute>
  );
}

function App() {
  return (
    <BrowserRouter>
      <Navbar />
      <main className="container">
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/services" element={<Services />} />
            <Route path="/gallery" element={<Gallery />} />
            <Route path="/contactus" element={<ContactUs />} />
            <Route path="/course/:id" element={<CourseDetails />} />

            {/* Admin routes */}
            <Route path="/admin" element={<AdminLogin />} />
            <Route
              path="/admin/dashboard"
              element={
                <AdminRoute>
                  <AdminDashboard />
                </AdminRoute>
              }
            />

            {/* Student routes */}
            <Route path="/student/login" element={<StudentLogin />} />
            <Route
              path="/student/dashboard"
              element={
                <StudentRoute>
                  <StudentDashboard />
                </StudentRoute>
              }
            />
            <Route
              path="/student/academic-history"
              element={
                <StudentRoute>
                  <StudentAcademicHistory />
                </StudentRoute>
              }
            />

            {/* Instructor routes */}
            <Route path="/instructor/login" element={<InstructorLogin />} />
            <Route
              path="/instructor/dashboard"
              element={
                <InstructorRoute>
                  <InstructorDashboard />
                </InstructorRoute>
              }
            />
            <Route
              path="/instructor/force-password-change"
              element={
                <InstructorRoute>
                  <InstructorForcePasswordChange />
                </InstructorRoute>
              }
            />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </main>
      <Footer />
    </BrowserRouter>
  );
}

export default App;
